// Created: 2026-07-31
// Updated: 2026-09-03
//
// TombstonePlaceholder — the "content is not available here" surface, in THREE variants
// (content-lifecycle-and-deletion, task 14.5 / Req 3.1-3.4; content-visibility-model task 32 / Req 9.6-9.8).
//
//  - `variant="page"` (default): a full-page placeholder for a detail page whose read resolved to a
//    200 tombstone (pattern / project / collection / material).
//  - `variant="entry"`: a compact inline entry for a listing / reference / thread row, so a
//    tombstoned item is shown as "removed by author" rather than omitted silently or linked to a
//    dead page.
//  - `variant="masked-entry"`: the masked reference-surface entry a surface OWNER sees in place of a
//    member whose Access_Level is now `private` (content-visibility-model Requirement 9.6). Requirement 9.7
//    requires this to be a variant of THIS component rather than a new one, so the unavailable surfaces
//    cannot drift apart visually.
//
// ─── WHY THE MASKED VARIANT HAS NO TITLE ──────────────────────────────────────────────────────────
//
// Requirement 9.6 reads "retains the title". IT IS NOT IMPLEMENTED LITERALLY, and that is a decision taken
// twice on the backend, not an omission here.
//
// `buildMaskedReferenceEntry` (`packages/lambda-utilities/src/utils/reference-surface.ts`) is the authority
// for what a masked entry contains, and it is built from an ALLOW-LIST of exactly two fields:
// `{ pattern_id, masked: true }`. Task 22 established that shape and task 47 kept it, on this reasoning:
//
//   * A reference surface stores only ids. Favorites rows and collection member rows carry no historical
//     title, so there is no "the title as the owner saved it" to retain.
//   * The CURRENT title of a now-private pattern is the wrong thing to show even where it is reachable. It
//     is either stale — the author may have renamed the item after hiding it — or it is a leak of a string
//     that was never public, to a caller who is a third party to the CONTENT even while owning the SURFACE.
//
// A property test pins that shape (`reference-surface-member-rules.property.test.ts` asserts the entry's
// keys are exactly `['masked', 'pattern_id']`), so widening it would be a backend change with its own test
// to update — not something this component can decide.
//
// So the label below says only what is actually known: the owner put this here, and can no longer open it.
// Task 32 reports Requirement 9.6 back for amendment rather than inventing a title or fetching a live one.
//
// ─── WHY REMOVAL IS OFFERED AND NOT PERFORMED ─────────────────────────────────────────────────────
//
// `private` is a REVERSIBLE state (Requirement 9.5): the author may make the item public again tomorrow, at
// which point the owner's saved reference resolves normally. Deleting the reference row on a reversible
// signal destroys the owner's own data for a condition that may not last, which is why Requirement 9.6 says
// removal is OFFERED and Requirement 9.8 forbids mutating anything as a side effect of a read. The control
// is therefore two-step: the first click only ARMS a confirmation that states what removal actually does,
// and only the confirm invokes `onRemove`.

import React, { useState } from 'react'

interface TombstonePlaceholderProps {
  /** Content noun for the copy, e.g. 'pattern', 'project', 'collection', 'material'. */
  contentNoun?: string
  variant?: 'page' | 'entry' | 'masked-entry'
  /**
   * `masked-entry` only. Names the LIST the reference row lives in — 'favorites', 'this collection' — so the
   * confirmation can say which of the owner's own rows is being dropped. Defaults to a neutral phrase
   * because the surface is not always known to the caller.
   */
  referenceSurfaceNoun?: string
  /**
   * `masked-entry` only. Invoked when the owner CONFIRMS removal. Absent means no control is offered, which
   * is the correct rendering on a surface that has no removal mutation wired.
   */
  onRemove?: () => void
  /** `masked-entry` only. Disables the confirm while the removal mutation is in flight. */
  removePending?: boolean
}

/** Shared by the two inline variants, so an unavailable row looks the same whichever reason produced it. */
const ENTRY_FRAME: React.CSSProperties = {
  padding: '12px 16px',
  border: '1px dashed #cbd2d9',
  borderRadius: '6px',
  color: '#5f6b7a',
  background: '#f8f9fa',
}

const ACTION_BUTTON: React.CSSProperties = {
  border: '1px solid #cbd2d9',
  borderRadius: '4px',
  background: '#ffffff',
  color: '#5f6b7a',
  padding: '4px 10px',
  font: 'inherit',
  cursor: 'pointer',
}

export const TombstonePlaceholder: React.FC<TombstonePlaceholderProps> = ({
  contentNoun = 'content',
  variant = 'page',
  referenceSurfaceNoun = 'your saved list',
  onRemove,
  removePending = false,
}) => {
  // Declared unconditionally, above the variant branches, per the Rules of Hooks. It is state local to the
  // masked variant's two-step control and is never read by the other two.
  const [confirming, setConfirming] = useState(false)

  if (variant === 'masked-entry') {
    return (
      <div data-testid="masked-entry" style={ENTRY_FRAME}>
        {/*
          Stands in for the title. There is none to render — see the module header. The parenthetical is
          part of the label rather than a separate badge so it cannot be styled away from the name it
          qualifies.
        */}
        <div data-testid="masked-entry-label" style={{ fontWeight: 600, marginBottom: '4px' }}>
          Saved {contentNoun} (no longer available)
        </div>

        {/*
          The unavailable notice, deliberately WORDED APART from the tombstone variant's. They mean
          different things: tombstoned is "the author deleted it", masked is "you can no longer open it, and
          it may come back".
        */}
        <p data-testid="masked-entry-notice" style={{ margin: 0, fontStyle: 'italic' }}>
          The author made this {contentNoun} private, so you can no longer open it. Your entry in{' '}
          {referenceSurfaceNoun} is kept until you remove it.
        </p>

        {onRemove &&
          (confirming ? (
            <div data-testid="masked-entry-confirm" style={{ marginTop: '10px' }}>
              <p style={{ margin: '0 0 8px 0' }}>
                Removing this deletes only your {referenceSurfaceNoun} entry. The {contentNoun} itself is not
                deleted, and its author is not told.
              </p>
              <button
                type="button"
                data-testid="masked-entry-confirm-remove"
                style={ACTION_BUTTON}
                disabled={removePending}
                onClick={() => {
                  onRemove()
                  setConfirming(false)
                }}
              >
                Yes, remove it
              </button>{' '}
              <button
                type="button"
                data-testid="masked-entry-cancel"
                style={ACTION_BUTTON}
                onClick={() => setConfirming(false)}
              >
                Keep it
              </button>
            </div>
          ) : (
            <button
              type="button"
              data-testid="masked-entry-remove"
              style={{ ...ACTION_BUTTON, marginTop: '10px' }}
              onClick={() => setConfirming(true)}
            >
              Remove from {referenceSurfaceNoun}
            </button>
          ))}
      </div>
    )
  }

  if (variant === 'entry') {
    return (
      <div data-testid="tombstone-entry" style={{ ...ENTRY_FRAME, fontStyle: 'italic' }}>
        This {contentNoun} was removed by its author.
      </div>
    )
  }

  return (
    <div
      data-testid="tombstone-placeholder"
      style={{
        maxWidth: 640,
        margin: '48px auto',
        padding: '32px',
        textAlign: 'center',
        border: '1px dashed #cbd2d9',
        borderRadius: '8px',
        color: '#5f6b7a',
        background: '#f8f9fa',
      }}
    >
      <h2 style={{ margin: '0 0 8px 0' }}>Removed by author</h2>
      <p style={{ margin: 0 }}>This {contentNoun} was removed by its author and is no longer available.</p>
    </div>
  )
}

export default TombstonePlaceholder
